import { Directive, ElementRef, OnChanges, Renderer2, inject, input } from '@angular/core';

@Directive({
  selector: '[gsImageAccessibility]',
  standalone: true
})
/**
 * Ensures images have accessible ALT text and an SEO description.
 * - If no ALT is provided, sets alt="" and hides from SR (decorative image).
 * - Adds a `data-seo-description` attribute for SEO tooling.
 */
export class ImageAccessibilityDirective implements OnChanges {
  /** Alternative text for screen readers. If omitted, alt="" is applied and image is hidden from screen readers. */
  readonly gsImgAlt = input<string>();
  /** Optional SEO description stored as `data-seo-description` for external crawlers or tooling. */
  readonly gsImgSeo = input<string>();

  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  ngOnChanges() {
    const imgElement = this.el.nativeElement as HTMLImageElement;
    // Set ALT attribute for accessibility
    /**
     * Ensure the `alt` attribute is present; add default and hide from SR if missing.
     */
    const alt = (this.gsImgAlt() ?? '').trim();
    if (alt.length > 0) {
      this.renderer.setAttribute(imgElement, 'alt', alt);
      this.renderer.removeAttribute(imgElement, 'aria-hidden');
    } else {
      // Decorative image fallback (no hardcoded language)
      this.renderer.setAttribute(imgElement, 'alt', '');
      this.renderer.setAttribute(imgElement, 'aria-hidden', 'true');
    }
    // Set SEO description as data attribute
    /**
     * Add an SEO description as a data attribute; fall back to a safe default.
     */
    const seo = (this.gsImgSeo() ?? '').trim();
    if (seo.length > 0) {
      this.renderer.setAttribute(imgElement, 'data-seo-description', seo);
    } else {
      this.renderer.setAttribute(imgElement, 'data-seo-description', '');
    }
  }
}
